import { cs } from "../../utils/currency";
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import VideoLoader from "../../components/VideoLoader";
import api from "../../services/api";
import { useOrder } from "./OrderContext";
import { printUniversal } from "./universalPrint";
import { buildReceiptSummary, ReceiptOrder } from "./receiptPrint";
import { useEmpOrderOptional } from "../../Salespos/EmpOrderContext";

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface OrderItem {
  itemId: string;
  name: string;
  quantity: number;
  price: number;
  tax:number;
  gst?: number;
  pst?: number;
  itemstatus?: string;
  discountAmount?: number;
  customOptions?: { label: string; price: number; qty: number }[];
}

interface Order {
  _id: string;
  orderNumber: string;
  items: OrderItem[];
  totalAmount: number;
  status: string;
  discountAmount: number;
  createdAt: string;
  phoneNumber?: string;
  tableToken?: number;
  orderBy: string;
  employeeName?: string;
  payChoice?: string;
  choice?: string;
  orderStatus?: string;
  timestamp?: string;
  pendingLoyaltyBonus?: number;
}

const Confirmation: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionLogoUrl, setSessionLogoUrl] = useState("");
  const { clearOrder, logoUrl } = useOrder();
  const empOrder = useEmpOrderOptional();
  // Sales POS: email receipt (prefilled with the loyalty member's email)
  const location = useLocation();
  const [receiptEmail, setReceiptEmail] = useState<string>(
    () => (location.state as { email?: string } | null)?.email || "",
  );
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleEmailReceipt = async () => {
    if (!order || !receiptEmail.trim()) return;
    setEmailSending(true);
    try {
      await api.post(`/orders/${order._id}/email-receipt`, { email: receiptEmail.trim() });
      setEmailSent(true);
      toast.success("Receipt emailed");
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Couldn't send the receipt");
    } finally {
      setEmailSending(false);
    }
  };

  const handleClick = () => {
    if (order?.orderBy === "customer") {
      navigate("/choice");
    } else {
      navigate("/sales");
    }
  };
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     if (order?.orderBy === "customer") {
  //       navigate("/choice");
  //     } else {
  //       navigate("/sales");
  //     }
  //   }, 15000);

  //   return () => clearTimeout(timer);
  // }, [navigate, order?.orderBy]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      if (order?.orderBy === "customer") {
        navigate("/choice", { replace: true });
      } else {
        navigate("/sales", { replace: true });
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.history.pushState(null, "", window.location.href);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate, order?.orderBy]);

  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      const response = await api.get<ApiResponse<Order>>(`/orders/${orderId}`);
      if (response.data.success && response.data.data) {
        setOrder(response.data.data);
      } else {
        setError("Failed to fetch order details");
        // toast.error("Failed to fetch order details");
      }
    } catch (err) {
      setError("Failed to fetch order details");
      // toast.error("Failed to fetch order details");
      
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  useEffect(() => {
    if (order) clearOrder();
  }, [order, clearOrder]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = order?.orderBy === "employee" ? "emp_logo_url" : "logo_url";
    setSessionLogoUrl(window.sessionStorage.getItem(key) || "");
  }, [order?.orderBy]);

  const handlePrintReceipt = useCallback(async () => {
    if (!order) return;
    const receiptLogoUrl =
      order.orderBy === "employee" && empOrder?.logoUrl
        ? empOrder.logoUrl
        : logoUrl;

    // Fetch pre-rendered ESC/POS logo base64 from server
    let logoBase64: string | null = null;
    try {
      const resp = await api.get<{ success: boolean; logoBase64: string | null }>(
        "/platform/logo-escpos",
      );
      logoBase64 = resp.data.logoBase64;
    } catch {
      // logo fetch failure doesn't block printing
    }

    try {
      await printUniversal(order, logoBase64 ?? undefined, receiptLogoUrl);
    } catch (err) {
      console.warn("[Print] printUniversal failed:", err);
    }
  }, [order, logoUrl, empOrder?.logoUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <VideoLoader />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="text-center p-2">
        <p>{error || "Order not found"}</p>
        <button onClick={() => navigate(-1)} className="return-btn">
          Return
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[600px] mx-auto p-8 text-center bg-white ">
      <div className="bg-white p-6 rounded-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.1)]">
        {sessionLogoUrl ? (
          <div className="mb-4 flex justify-center">
            <img
              src={sessionLogoUrl}
              alt="Logo"
              className="max-h-28 w-auto object-contain"
            />
          </div>
        ) : null}
        <div
          className={`
    p-4 rounded-lg mb-6 text-center 
    ${
      order.orderBy === "employee"
        ? "bg-[#007bff] text-white"
        : "bg-main text-white"
    }
  `}
        >
          <h2>Order #{order.orderNumber}</h2>
          <p className="m-0">{new Date(order.createdAt).toLocaleString()}</p>
        </div>

        <div className="mb-6">
          <h3
            className={
              order.orderBy === "employee" ? "text-black" : "text-main"
            }
          >
            Thanks For Ordering!
          </h3>
         

          <div className="flex flex-col gap-1">
            {order.phoneNumber && (
              <div className="bg-[#f7f7f7] p-3 rounded-md text-left">
                <strong>Phone Number:</strong> {order.phoneNumber}
              </div>
            )}

            {order.tableToken && (
              <div className="bg-[#f7f7f7] p-3 rounded-md text-left">
                <strong>Table Token:</strong> {order.tableToken}
                <p className="m-0">
                  <strong>Order By: </strong>
                  {order.orderBy === "employee"
                    ? order.employeeName || "Unknown Employee"
                    : "Customer"}{" "}
                  {order.payChoice && (
                    <>
                      <br />
                      <strong>Payment mode: </strong> {order.payChoice}
                    </>
                  )}
                </p>
              </div>
            )}

            {!order.phoneNumber && !order.tableToken && (
              <div className="text-[#fff] p-3 rounded-md text-center bg-[#fbbf24]">
                <strong>Note:</strong> No contact information provided
              </div>
            )}
          </div>
        </div>

        <h2 className="mt-8 mb-4 text-left">Order Items</h2>
        <div className="mb-8">
          {order.items.map((item, index) => (
            <div key={index} className="item-row flex justify-between flex-col">
              <div className="item-main flex justify-between w-full">
                <span>
                  {item.name} x {item.quantity}
                </span>
                <span> ${(item.price * item.quantity).toFixed(2)}</span>
              </div>

                     {(() => {
                const opts = item.customOptions ?? [];

                const removedItems = opts
                  .filter(opt => opt.qty === -1)
                  .map(opt => opt.label);

                const normalItems = opts
                  .filter(opt => opt.qty !== -1)
                  .map(opt =>
                    opt.qty > 1 ? `${opt.label} ×${opt.qty}` : opt.label
                  );

                return (
                  <div className="text-left">
                    {normalItems.length > 0 && (
                      <p className="mb-0 text-xs">Added- {normalItems.join(", ")}</p>
                    )}
                    {removedItems.length > 0 && (
                      <p className="text-xs mb-1">Removed- {removedItems.join(", ")}</p>
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
<div className="border-t">
          {/* Same lines as the printed receipt (GST/PST, discount, delivery, tip, adjustment) */}
          {(() => {
            const summary = buildReceiptSummary(order as unknown as ReceiptOrder);
            return (
              <>
                {summary.lines.map((line, idx) => (
                  <div
                    key={line.label}
                    className={`flex justify-between text-[1.2rem] font-bold mt-2 ${
                      idx === 0 ? "pt-2 border-t-2 border-gray-200" : ""
                    }`}
                  >
                    <span>{line.label}:</span>
                    <span>{line.value}</span>
                  </div>
                ))}
                <div className="flex justify-between text-[1.2rem] font-bold">
                  <span>Total:</span>
                  <span>{cs()}{summary.total.toFixed(2)}</span>
                </div>
              </>
            );
          })()}
</div>
        {order.orderBy === "employee" && (
          <div className="mt-4 flex flex-col gap-1 rounded-md bg-[#f7f7f7] p-3 text-left text-sm">
            <div>
              <strong>Paid by:</strong> {order.payChoice === "card" ? "Card (Square)" : "Cash"}
            </div>
            {order.choice && (
              <div>
                <strong>Order type:</strong> {order.choice}
              </div>
            )}
            {(order.pendingLoyaltyBonus ?? 0) > 0 ? (
              <div className="font-semibold text-green-700">
                Cashback earned: {cs()}{Number(order.pendingLoyaltyBonus).toFixed(2)}
                <span className="font-normal text-gray-600"> (added to the customer's wallet when the order is completed)</span>
              </div>
            ) : (
              !order.phoneNumber && <div className="text-gray-500">No loyalty on this order</div>
            )}
            <div className="mt-2 flex gap-2">
              <input
                type="email"
                value={receiptEmail}
                onChange={(e) => {
                  setReceiptEmail(e.target.value);
                  setEmailSent(false);
                }}
                placeholder="Customer email for receipt"
                className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none"
              />
              <button
                type="button"
                onClick={handleEmailReceipt}
                disabled={emailSending || !receiptEmail.trim()}
                className="shrink-0 rounded-lg bg-[#2563eb] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {emailSending ? "Sending..." : emailSent ? "Sent ✓" : "Email Receipt"}
              </button>
            </div>
          </div>
        )}

        <button
          onClick={handlePrintReceipt}
          className="w-full p-4 mt-4 rounded-lg cursor-pointer text-base font-medium border border-main text-main bg-white"
        >
          Print Receipt
        </button>

        <button
          onClick={handleClick}
          className={`
    w-full p-4 mt-6 rounded-lg cursor-pointer text-base font-medium
    ${
      order.orderBy === "employee"
        ? "bg-[#007bff] text-white"
        : "bg-main text-white"
    }
  `}
        >
          DONE
        </button>
      </div>
    </div>
  );
};

export default Confirmation;
